import { useAuth } from '../AuthContext';

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MainMenu from '../MainMenu';
import CatchGallery from './CatchGallery';
import { useParams, useNavigate } from 'react-router-dom';
import { updateMyProfile, uploadProfilePicture, fetchBuddyRelationship, sendBuddyInvite, fetchUserCatchStats, fetchProfileInitData } from '../api';
import { API_URL } from '../config';

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, token, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [dataDialogOpen, setDataDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    email: '',
    nickname: '',
    password: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [relationship, setRelationship] = useState(null);
  const [relLoading, setRelLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState('');

  // Load profile, relationship, and stats in one batch call (3→1)
  useEffect(() => {
    if (!token || !userId) return;
    setLoading(true);
    setRelLoading(true);
    
    fetchProfileInitData(token, userId)
      .then(data => {
        setProfile(data.profile);
        setRelationship(data.relationship);
        setStats(data.stats);
        setStatsError('');
      })
      .catch(() => {
        // Fallback to individual profile fetch
        const headers = { Authorization: `Bearer ${token}` };
        fetch(`${API_URL}/profiles/users/${userId}`, { headers })
          .then(res => (res.ok ? res.json() : null))
          .then(data => setProfile(data))
          .catch(() => setProfile(null));
        setRelationship(null);
        setStats(null);
      })
      .finally(() => {
        setLoading(false);
        setRelLoading(false);
      });
  }, [userId, token]);

  const isOwnProfile = user && String(user.id) === String(userId);
  const displayProfile = profile || (isOwnProfile ? user : null);

  const openDataDialog = () => {
    if (!isOwnProfile) return;
    const source = profile || user || {};
    setFormValues({
      email: source.email || '',
      nickname: source.nickname || '',
      password: '',
    });
    setError('');
    setDataDialogOpen(true);
  };

  const closeDataDialog = () => {
    if (saving) return;
    setDataDialogOpen(false);
  };

  const openImageDialog = () => {
    if (!isOwnProfile) return;
    setImageFile(null);
    setImagePreview(null);
    setError('');
    setImageDialogOpen(true);
  };

  const closeImageDialog = () => {
    if (saving) return;
    setImageDialogOpen(false);
  };

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    setImageFile(file || null);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSaveData = async () => {
    if (!token || !isOwnProfile) return;
    setSaving(true);
    setError('');
    try {
      const profilePayload = {
        email: formValues.email,
        nickname: formValues.nickname,
        password: formValues.password || undefined,
      };

      const updated = await updateMyProfile(token, profilePayload);
      setProfile(updated);
      if (login) {
        login(updated, token);
      }
      setDataDialogOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveImage = async () => {
    if (!token || !isOwnProfile || !imageFile) {
      setImageDialogOpen(false);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await uploadProfilePicture(token, imageFile);
      setProfile(updated);
      if (login) {
        login(updated, token);
      }
      setImageDialogOpen(false);
    } catch (e) {
      setError(e.message || 'Failed to update profile picture');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!token || isOwnProfile || !userId) return;
    try {
      const parsedId = parseInt(userId, 10);
      if (!Number.isFinite(parsedId)) return;
      const res = await sendBuddyInvite(token, parsedId);
      setRelationship({ relationship: 'pending_outgoing', request_id: res?.request_id ?? null });
    } catch (e) {
      setError(e.message || 'Failed to send buddy invite');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, position: 'relative' }}>
      {isOwnProfile && (
        <MainMenu
          sx={{ position: 'absolute', top: 8, right: 8 }}
          onEditProfile={openDataDialog}
        />
      )}
      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
        displayProfile ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar src={(() => {
                const pic = displayProfile && displayProfile.profile_picture;
                if (!pic) return undefined;
                if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                return `${API_URL}/${normalized}`;
              })()} sx={{ width: 80, height: 80 }}>
                {!(displayProfile && displayProfile.profile_picture) && (
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="20" fill="#bdbdbd" />
                    <text x="50%" y="55%" textAnchor="middle" fill="#fff" fontSize="18" fontFamily="Arial" dy=".3em">?</text>
                  </svg>
                )}
              </Avatar>
              {isOwnProfile && (
                <IconButton
                  size="small"
                  onClick={openImageDialog}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: -8,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              </Box>
              <Typography variant="h5">{displayProfile.nickname || displayProfile.email || 'Unknown'}</Typography>
              {!isOwnProfile && (
                <Box sx={{ mt: 1 }}>
                  {relationship?.relationship === 'friend' ? (
                    <Typography variant="body2" color="success.main">
                      You are buddies
                    </Typography>
                  ) : relationship?.relationship === 'pending_outgoing' ? (
                    <Typography variant="body2" color="warning.main">
                      Buddy request sent
                    </Typography>
                  ) : relationship?.relationship === 'pending_incoming' ? (
                    <Typography variant="body2" color="info.main">
                      This user requested you as buddy
                    </Typography>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSendInvite}
                    >
                      Add to buddies
                    </Button>
                  )}
                </Box>
              )}
              {/* Do not show email address on the public profile header */}
            </Box>
            {stats && (
              <Box
                sx={{
                  mt: 3,
                  mb: 2,
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(212, 175, 55, 0.5)',
                  boxShadow: '0 0 16px rgba(212, 175, 55, 0.12)',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                      Total length
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {((stats.total_length_cm || 0) / 100).toFixed(2)} m
                    </Typography>
                  </Box>
                  {stats.biggest_catch && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                        Biggest fish
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {stats.biggest_catch.species}
                      </Typography>
                      <Typography variant="body2">{stats.biggest_catch.size} cm</Typography>
                    </Box>
                  )}
                </Box>

                {Array.isArray(stats.per_species) && stats.per_species.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      rowGap: 2,
                      columnGap: 1,
                    }}
                  >
                    {stats.per_species
                      .slice()
                      .sort((a, b) => (b.best_size || 0) - (a.best_size || 0))
                      .map((item) => {
                        const key = item.species.toLowerCase();
                        const iconSrc =
                          key === 'hecht'
                            ? '/fish-icons/hecht.png'
                            : key === 'barsch'
                            ? '/fish-icons/barsch.png'
                            : key === 'zander'
                            ? '/fish-icons/zander.png'
                            : '/fish-icons/default.png';
                        return (
                          <Box
                            key={item.species}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flex: '1 1 45%',
                              minWidth: 0,
                            }}
                          >
                            <Box
                              component="img"
                              src={iconSrc}
                              alt={item.species}
                              sx={{
                                width: 32,
                                height: 32,
                                objectFit: 'contain',
                                filter:
                                  'grayscale(1) sepia(1) hue-rotate(20deg) saturate(5) brightness(1.1) drop-shadow(0 1px 2px rgba(212, 175, 55, 0.6))',
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                {item.species}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {item.best_size} cm
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                )}
              </Box>
            )}
            <CatchGallery userId={userId} />

            <Dialog open={dataDialogOpen} onClose={closeDataDialog} fullWidth maxWidth="sm">
              <DialogTitle>Change personal data</DialogTitle>
              <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField
                    label="Email"
                    type="email"
                    value={formValues.email}
                    onChange={handleChange('email')}
                    fullWidth
                  />
                  <TextField
                    label="Nickname"
                    value={formValues.nickname}
                    onChange={handleChange('nickname')}
                    fullWidth
                  />
                  <TextField
                    label="New Password (optional)"
                    type="password"
                    value={formValues.password}
                    onChange={handleChange('password')}
                    fullWidth
                    helperText="Leave empty to keep your current password"
                  />
                  {error && (
                    <Typography color="error" variant="body2">
                      {error}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDataDialog} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveData} variant="contained" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={imageDialogOpen} onClose={closeImageDialog} fullWidth maxWidth="xs">
              <DialogTitle>Change profile picture</DialogTitle>
              <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, alignItems: 'center' }}>
                  <Button variant="contained" component="label">
                    Choose image
                    <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                  </Button>
                  {imagePreview && (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        style={{ maxWidth: 200, maxHeight: 200, borderRadius: '50%' }}
                      />
                    </Box>
                  )}
                  {error && (
                    <Typography color="error" variant="body2">
                      {error}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeImageDialog} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveImage} variant="contained" disabled={saving || !imageFile}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <Typography color="error" align="center" sx={{ mt: 4 }}>User not found.</Typography>
        )
      )}
    </Container>
  );
}
